import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import ControlPanel from "../components/ControlPanel";
import LiveTranscript from "../components/LiveTranscript";
import Summary from "../components/Summary";
import KeywordSection from "../components/KeywordSection";
import PricingOverlay from "../components/PricingOverlay";
import { APP_URL, PaymentPlan } from "../config";
import useToggleTranscription from "../hooks/useToggleTranscription";
import useTranscription from "../hooks/useTranscription";
import usePaymentHandler from "../hooks/usePaymentHandler";
import useCreditManagement from "../hooks/useCreditManagement";
import useKeywordManagement from "../hooks/useKeywordManagement";
import BottomTabNavigation from "../components/BottomTabNavigation";
import { storage as localStorage } from "../utils/storage";
import useMicrophoneAudio from "../hooks/useMicrophoneAudio";

export default function HomePage() {
  const [activeTab, setActiveTab] = useState("transcript");
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const { isTranscriptionActive, toggleTranscription } =
    useToggleTranscription();
  const {
    transcription,
    interimTranscription,
    handleCopyToClipboard,
    handleClearText,
    handleTranscription,
  } = useTranscription();
  const { inputVolume, micDevices, selectedMic, setSelectedMic } =
    useMicrophoneAudio(isTranscriptionActive, handleTranscription);

  const [remainingCredits, setRemainingCredits] = useState<number>(0);
  const [noCredits, setNoCredits] = useState<boolean>(false);
  const { loading, fetchTotals } = useCreditManagement(
    setRemainingCredits,
    setNoCredits
  );

  const { keywords, newKeyword, setNewKeyword, addKeyword, removeKeyword } =
    useKeywordManagement();

  useEffect(() => {
    if (noCredits) {
      setIsOverlayOpen(true);
    }
  }, [noCredits]);

  usePaymentHandler({ 
    fetchTotals, 
    setIsOverlayOpen 
  });

  const handlePurchaseCredits = async (plan: keyof typeof PaymentPlan) => {
    const uid = await localStorage.get("id");
    if (!uid) return;
    window.open(`${APP_URL}/payment-handler/${uid}/${plan}`, "_blank");
    // window.electron.ipcRenderer.sendMessage('open-external-url', `${APP_URL}/payment-handler/${uid}/${plan}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "transcript":
        return (
          <LiveTranscript
            transcription={transcription}
            interimTranscription={interimTranscription}
            handleCopyToClipboard={handleCopyToClipboard}
            handleClearText={handleClearText}
            isActive={isTranscriptionActive}
          />
        );
      case "keywords":
        return (
          <KeywordSection
            keywords={keywords}
            newKeyword={newKeyword}
            setNewKeyword={setNewKeyword}
            addKeyword={addKeyword}
            removeKeyword={removeKeyword}
          />
        );
      case "summary":
        return (
          <Summary
            initialContent={transcription.join("\n")}
            disabled={noCredits}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <PricingOverlay
        isOpen={isOverlayOpen}
        remainingCredits={remainingCredits}
        onClose={() => setIsOverlayOpen(false)}
        onCreditPurchase={(plan: keyof typeof PaymentPlan) =>
          handlePurchaseCredits(plan)
        }
      />
      <div className="bg-gray-100 p-4 space-y-4 app-container">
        <Header
          openPricing={() => setIsOverlayOpen(true)}
          remainingCredits={remainingCredits}
          loading={loading}
        />
        <ControlPanel
          isActive={isTranscriptionActive}
          setIsActive={toggleTranscription}
          disabled={noCredits}
          inputVolume={inputVolume}
          selectedMic={selectedMic}
          setSelectedMic={setSelectedMic}
          micDevices={micDevices}
        />
        <div className="flex flex-1 space-x-2">{renderContent()}</div>
      </div>
      <BottomTabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
    </>
  );
}
